import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Popconfirm, message, Upload, Image, Collapse } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, GlobalOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import API from '../api/client';
import { BACKEND_URL } from '../../config';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean'],
  ],
};

const categories = ['Etkinlik', 'Menü', 'Haber', 'İlan', 'Duyuru', 'Kampanya'];

const AnnouncementsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try { const res = await API.get('/announcements'); setData(res.data); }
    catch { message.error('Veriler yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setImageUrl('');
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record._id);
    setImageUrl(record.image || '');
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/upload', formData);
      const url = `${BACKEND_URL}${res.data.url}`;
      setImageUrl(url);
      form.setFieldsValue({ image: url });
      message.success('Görsel yüklendi');
    } catch {
      message.error('Görsel yüklenemedi');
    }
    setUploading(false);
    return false;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await API.put(`/announcements/${editingId}`, values);
        message.success('Güncellendi');
      } else {
        await API.post('/announcements', values);
        message.success('Eklendi');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation */ }
  };

  const handleDelete = async (id) => {
    await API.delete(`/announcements/${id}`);
    message.success('Silindi');
    fetchData();
  };

  const columns = [
    {
      title: 'Görsel', dataIndex: 'image', key: 'image', width: 80,
      render: (url) => url ? <Image src={url} width={60} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} /> : '-',
    },
    { title: 'Başlık', dataIndex: 'title', key: 'title' },
    {
      title: 'Slug', dataIndex: 'slug', key: 'slug',
      render: (slug) => slug ? <span style={{ fontSize: 12, color: '#888' }}>/duyurular/{slug}</span> : '-',
    },
    { title: 'Kategori', dataIndex: 'category', key: 'category' },
    { title: 'Tarih', dataIndex: 'date', key: 'date' },
    {
      title: 'İşlem', key: 'actions', width: 120,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Duyuru Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Duyuru</Button>
      </div>
      <Table columns={columns} dataSource={data} rowKey="_id" loading={loading} />
      <Modal
        title={editingId ? 'Duyuru Düzenle' : 'Yeni Duyuru'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={700}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Başlık" rules={[{ required: true, message: 'Başlık gerekli' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="slug" label="URL Slug" extra="Boş bırakılırsa başlıktan otomatik oluşturulur. Örn: yaz-sezonu-acilis-etkinligi">
            <Input addonBefore="/duyurular/" placeholder="otomatik-olusturulur" />
          </Form.Item>
          <Form.Item name="summary" label="Özet (Liste görünümü)">
            <Input.TextArea rows={2} placeholder="Listeleme sayfasında görünecek kısa açıklama" />
          </Form.Item>
          <Form.Item name="content" label="Detay İçeriği">
            <ReactQuill theme="snow" modules={quillModules} placeholder="Duyuru detay sayfasında görünecek tam içerik..." style={{ minHeight: 200 }} />
          </Form.Item>
          <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Tarih gerekli' }]}>
            <Input placeholder="10 Mart 2026" />
          </Form.Item>
          <Form.Item name="category" label="Kategori" rules={[{ required: true, message: 'Kategori gerekli' }]}>
            <Select options={categories.map(c => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="image" label="Görsel">
            <Input style={{ display: 'none' }} />
          </Form.Item>
          <div style={{ marginBottom: 16, marginTop: -8 }}>
            <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading}>Görsel Yükle</Button>
            </Upload>
            {imageUrl && (
              <div style={{ marginTop: 8 }}>
                <Image src={imageUrl} width={200} style={{ borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>

          <Collapse
            ghost
            items={[{
              key: 'seo',
              label: <span><GlobalOutlined /> SEO Ayarları</span>,
              children: (
                <>
                  <Form.Item name="metaTitle" label="Meta Başlık" extra="Tarayıcı sekmesinde ve arama sonuçlarında görünecek başlık">
                    <Input placeholder="Sayfa başlığı | Dihiz" />
                  </Form.Item>
                  <Form.Item name="metaDescription" label="Meta Açıklama" extra="Arama motorlarında görünecek açıklama (max 160 karakter)">
                    <Input.TextArea rows={2} placeholder="Bu duyuru hakkında kısa açıklama..." maxLength={160} showCount />
                  </Form.Item>
                  <Form.Item name="metaKeywords" label="Anahtar Kelimeler" extra="Virgülle ayırarak yazın">
                    <Input placeholder="dihiz, didim, duyuru, etkinlik" />
                  </Form.Item>
                </>
              ),
            }]}
          />
        </Form>
      </Modal>
    </>
  );
};

export default AnnouncementsPage;
